import { PageType } from '@zotlo-shared/types/model';
import { parseQueryString } from './queryString';

const languages = new Set([
  "tr", "en", "de", "ru", "ar", "ja", "fr", "kk", "az",
  "cs", "he", "id", "it", "ms", "nl", "no", "pl", "pt",
  "pt_br", "ro", "sv", "th", "tl", "uk", "ur", "vi", "zh",
  "zh_tw", "da", "es", "hi", "hu", "ko"
]);

export function isAvailablePage(pageType: string) {
  const availablePages = Object.values(PageType);
  return availablePages.includes(pageType as PageType);
}

function isActiveLang(lang: string) {
  return languages.has(lang);
}

export function getBaseUrl(currentPath?: string) {
  const { pathname = '', search = '', protocol = '', host = '' } = globalThis?.location || {};
  const [restPath, hash = '' ] = (currentPath || pathname).split('#');
  const [pathParse = '', queryString = ''] = (restPath || '')?.split('?');
  const pathList = pathParse.split('/').filter(Boolean).reverse();
  const [first = '', second = ''] = pathList;

  let slug = [...pathList].reverse().join('/');
  let lang = '';
  let pageType = '';

  if (isActiveLang(first)) {
    const [, ...restOfPath] = pathList;
    slug = restOfPath.reverse().join('/');
    lang = first;
    pageType = '';
  } else if (isActiveLang(second)) {
    const [, , ...restOfPath] = pathList;
    slug = restOfPath.reverse().join('/');
    lang = second;
    pageType = isAvailablePage(first) ? first : '';
  } else if (isAvailablePage(first)) {
    const [, ...restOfPath] = pathList;
    slug = restOfPath.reverse().join('/');
    lang = '';
    pageType = first;
  }

  if (slug === '/' || slug === '404') slug = '';
  const fullPath = [slug, lang].filter(Boolean).join('/');

  return { slug, lang, pageType, fullPath, query: queryString || search.replace(/^\?/, '') || '', hash, protocol, host };
}

export function goToPage(page: PageType, options?: { queryStr?: string; includeQueryString?: boolean, excludeQueryParams?: string[] }) {
  const redirectTo = getClientSidePageUrl(page, options);
  if (globalThis?.location?.href) globalThis.location.href = redirectTo;
  return redirectTo;
}

function getPreviewSearchParams(previewCountry = ''):URLSearchParams {
  const searchParams = new URLSearchParams({
    ...(previewCountry && { previewCountry }),
  });
  return searchParams;
}

export function getPageWithPreviewCountryUrl(page: PageType, previewCountry = '', currentPath?: string) {
  const searchParams = getPreviewSearchParams(previewCountry);
  return getClientSidePageUrl(page, { queryStr: searchParams.toString() ? `?${searchParams}` : ``, currentPath });
}

export function goToPageWithPreviewCountry(page: PageType, previewCountry = '') {
  const searchParams = getPreviewSearchParams(previewCountry);
  goToPage(page, { queryStr: searchParams.toString() ? `?${searchParams}` : `` });
}

export function getClientSidePageUrl(page: PageType, options?: { currentPath?: string; queryStr?: string; includeQueryString?: boolean, excludeQueryParams?: string[] }) {
  const { includeQueryString = true, excludeQueryParams, currentPath } = options || {};
  let { queryStr = '' } = options || {};
  const { fullPath, query, hash } = getBaseUrl(currentPath);
  let pathPrefix = fullPath === '/' ? '' : `/${fullPath}`;
  if (pathPrefix.slice(-1) === '/') pathPrefix = pathPrefix.slice(0, -1);
  if (excludeQueryParams && Array.isArray(excludeQueryParams)) {
    const filteredQueryString = Object.entries(parseQueryString(queryStr || query)).reduce((acc, [key, value]) => {
      if (key && !excludeQueryParams.includes(key)) {
        acc.push(`${key}=${value}`);
      }
      return acc;
    }, [] as string[]).join('&');

    if (filteredQueryString) queryStr = `?${filteredQueryString}`;
  }

  const fullQueryStr = (includeQueryString ? queryStr : '') + (hash ? `#${hash}` : '');
  return `${pathPrefix}${page && page !== PageType.LANDING ? `/${page}` : ``}${fullQueryStr}`;
}

export function addQueryParamsToUrl(url: string, params: Record<string, string>) {
  const urlObj = new URL(url);
  Object.keys(params).forEach((key) => urlObj.searchParams.set(key, params[key] as string));
  return urlObj.toString();
}

export function getSandboxUrl(params: { lang: string; country: string; }) {
  if (import.meta.server) return '';
  const { lang, country } = params || {};
  const { slug, pageType, protocol, host } = getBaseUrl();
  const hostUrl = `${protocol}//${host}/`;
  const path = `${slug}/${lang}/${pageType}`.split('/').filter(Boolean).join('/');
  const fullUrl = `${hostUrl}${path}`;
  return addQueryParamsToUrl(fullUrl, { previewCountry: country });
}
